/*
   How many pages a chosen PDF has, read in the browser.

   Only the overnight lane needs this: a review past the deployment's page
   threshold goes overnight without being asked, so a reviewer choosing between
   "now" and "overnight" is choosing on size. Nothing is uploaded to find out —
   the count comes from the file already in hand, so the answer is there before
   the review is submitted.
*/

import type { PDFDocumentLoadingTask } from "pdfjs-dist"
import * as React from "react"

export type PageCount =
  | { kind: "idle" }
  | { kind: "counting" }
  | { kind: "counted"; pages: number }
  /** Encrypted, malformed, or not a PDF. The review may still be submittable. */
  | { kind: "unreadable" }

export function usePageCount(file: File | null): PageCount {
  const [state, setState] = React.useState<PageCount>({ kind: "idle" })

  React.useEffect(() => {
    if (!file) {
      setState({ kind: "idle" })
      return
    }

    // A result that arrives for a file the reviewer has already replaced is
    // dropped rather than shown against the new one.
    let cancelled = false
    // The loading task, not the document: destroying it releases the worker's
    // copy of the file as soon as the page count is read, rather than holding a
    // 200-page label for as long as the intake stays open. Held in a list so
    // both exit paths release through one function, whether or not the task was
    // ever created.
    const tasks: PDFDocumentLoadingTask[] = []
    setState({ kind: "counting" })

    const release = (): void => {
      for (const task of tasks.splice(0)) {
        if (!task.destroyed) void task.destroy()
      }
    }

    void (async () => {
      try {
        const pdfjs = await import("pdfjs-dist")
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/build/pdf.worker.min.mjs",
          import.meta.url,
        ).toString()

        const bytes = new Uint8Array(await file.arrayBuffer())
        const task = pdfjs.getDocument({ data: bytes })
        tasks.push(task)
        const { numPages } = await task.promise
        if (cancelled) return
        setState({ kind: "counted", pages: numPages })
      } catch {
        // Cancelling destroys the task, and the rejection that causes lands
        // here — indistinguishable from a genuinely unreadable file, so only a
        // live effect reports one.
        if (cancelled) return
        setState({ kind: "unreadable" })
      } finally {
        release()
      }
    })()

    return () => {
      cancelled = true
      release()
    }
  }, [file])

  return state
}
